import { CircleAlertIcon } from 'lucide-react';
import { useState } from 'react';

import { useGoToActiveCompetition } from '@/competitions/_common/hooks/useGoToActiveCompetition.ts';
import { getRegisterSourceData } from '@/lib/analytics/registerSource.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWithConfig } from '@/lib/api/requests.ts';
import { Button } from '@/ui/button-2.tsx';

import useBrainSession from '../../../../../../services/BrainSession/useBrainSession.ts';
import {
  BlurryCircle,
  BlurryCircleIcon,
} from '../../../chat-answers/_common/blurry-icon-circle.tsx';
import { CardDialog } from '../../../chat-answers/_common/card-dialog.tsx';

import { useHasEnded } from './use-has-ended.ts';

export const CTA = (props: {
  className?: string;
  onClick?: () => void;
  activeCompetition: CompetitionWithConfig | undefined;
  displayWarningWhenKIn: string[] | undefined;
}) => {
  const { toggleChat } = useBrainSession();
  const ended = useHasEnded();
  const goToActiveCompetition = useGoToActiveCompetition(props.activeCompetition);
  const checkShouldAsk = () => {
    if (!props.displayWarningWhenKIn) {
      return false;
    }
    return props.displayWarningWhenKIn.includes(getRegisterSourceData().params.k);
  };
  const [isOpen, setIsOpen] = useState(false);

  if (ended) {
    return (
      <Button
        color="orange"
        className={props.className}
        onClick={() => {
          props.onClick?.();
          void goToActiveCompetition();
        }}
      >
        Se den nye trekningen
      </Button>
    );
  }

  return (
    <>
      <Button
        color="orange"
        className={props.className}
        onClick={() => {
          props.onClick?.();
          if (checkShouldAsk()) {
            sendUserPortalEvent('sep26_conditions_notice_displayed');
            setIsOpen(true);
            return;
          }
          toggleChat({
            goal: 'september_2026_campaign',
            step: 'init',
          });
        }}
      >
        Søk og delta
      </Button>

      <CardDialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <CardDialog.Content>
          <CardDialog.Card className="text-center">
            <BlurryCircle blurry={false}>
              <BlurryCircleIcon>
                <CircleAlertIcon size={20} strokeWidth={2} aria-hidden="true" />
              </BlurryCircleIcon>
            </BlurryCircle>

            <CardDialog.Title className="mb-2 typography-v1-h3 text-pretty">
              Du må ha <span className="text-v1-primary-100">rentebærende</span> forbruks- eller
              kredittkortgjeld for å delta!
            </CardDialog.Title>

            <p className="mb-6 typography-v1-body4 text-pretty text-v1-neutral-100">
              For å delta i konkurransen må du søke om lavere rente på eksisterende forbruks- og
              kredittkortgjeld.
            </p>

            <div className="flex flex-col gap-2">
              <Button
                color="yellow"
                onClick={() => {
                  setIsOpen(false);
                  sendUserPortalEvent('sep26_conditions_notice_submitted');
                  toggleChat({
                    goal: 'september_2026_campaign',
                    step: 'init',
                  });
                }}
              >
                Jeg har forbruks- eller kredittkortgjeld
              </Button>
              <Button
                color="white"
                onClick={() => {
                  sendUserPortalEvent('sep26_conditions_notice_closed');
                  setIsOpen(false);
                }}
              >
                Lukk
              </Button>
            </div>
          </CardDialog.Card>
        </CardDialog.Content>
      </CardDialog.Root>
    </>
  );
};
